import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Rating,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Fade,
  Zoom,
  Slide,
  Backdrop,
  Dialog,
  DialogContent
} from '@mui/material';
import { 
  Send as SendIcon, 
  Feedback as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
  Celebration as CelebrationIcon
} from '@mui/icons-material';
import axios from 'axios';

const Feedback = () => {
  const [formData, setFormData] = useState({
    nickname: '',
    content: '',
    rating: 5
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
  const [errors, setErrors] = useState({});
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 清除对应字段的错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.content.trim()) {
      newErrors.content = '请输入您的意见或建议';
    } else if (formData.content.trim().length < 5) {
      newErrors.content = '意见内容至少需要5个字符';
    } else if (formData.content.trim().length > 2000) {
      newErrors.content = '意见内容不能超过2000个字符';
    }
    
    if (formData.nickname && formData.nickname.length > 50) {
      newErrors.nickname = '昵称不能超过50个字符';
    }
    
    if (formData.rating < 1 || formData.rating > 10) {
      newErrors.rating = '评分必须在1-10之间';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setAlert({ show: false, type: 'success', message: '' });
    
    try {
      const response = await axios.post('/api/feedback/submit', {
        nickname: formData.nickname.trim() || null,
        content: formData.content.trim(),
        rating: formData.rating
      });
      
      if (response.data.success) {
        // 显示成功动画
        setShowSuccessAnimation(true);
        setAnimationStep(1);
        
        // 延迟显示后续动画步骤
        setTimeout(() => setAnimationStep(2), 500);
        setTimeout(() => setAnimationStep(3), 1000);
        
        // 3秒后关闭动画并重置表单
        setTimeout(() => {
          setShowSuccessAnimation(false);
          setAnimationStep(0);
          setFormData({
            nickname: '',
            content: '',
            rating: 5
          });
          setAlert({
            show: true,
            type: 'success',
            message: response.data.message || '反馈提交成功，感谢您的宝贵意见！'
          });
        }, 3000);
      } else {
        throw new Error(response.data.message || '提交失败');
      }
    } catch (error) {
      console.error('提交反馈失败:', error);
      setAlert({
        show: true,
        type: 'error',
        message: error.response?.data?.detail || error.message || '提交失败，请稍后重试'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* 成功提交动画 */}
      <Dialog
        open={showSuccessAnimation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center',
            overflow: 'hidden'
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            overflow: 'hidden'
          },
          '& .MuiBackdrop-root': {
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 6, overflow: 'hidden' }}>
          <Box sx={{ position: 'relative' }}>
            {/* 庆祝图标动画 */}
            <Zoom in={animationStep >= 1} timeout={500}>
              <Box sx={{ mb: 3 }}>
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: '#4caf50',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                  }} 
                />
              </Box>
            </Zoom>
            
            {/* 成功文字动画 */}
            <Slide direction="up" in={animationStep >= 2} timeout={500}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                  提交成功！
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  感谢您的宝贵反馈
                </Typography>
              </Box>
            </Slide>
            
            {/* 庆祝特效 */}
            <Fade in={animationStep >= 3} timeout={500}>
              <Box sx={{ 
                position: 'absolute',
                top: -20,
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 40
              }}>
                🎉✨🎊
              </Box>
            </Fade>
            
            {/* 背景装饰 */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              pointerEvents: 'none'
            }} />
          </Box>
        </DialogContent>
      </Dialog>
      
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: 4
      }}>
        <Container maxWidth="md">
          <Paper 
            elevation={8} 
            sx={{ 
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Box sx={{ 
              textAlign: 'center', 
              mb: 5,
              position: 'relative'
            }}>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 3,
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
              }}>
                <FeedbackIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                用户反馈
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 500, 
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                您的意见对我们非常重要，请告诉我们您的想法和建议
              </Typography>
            </Box>

        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 3 }}
            onClose={() => setAlert({ show: false, type: 'success', message: '' })}
          >
            {alert.message}
          </Alert>
        )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="昵称（可选）"
                    placeholder="请输入您的昵称"
                    value={formData.nickname}
                    onChange={(e) => handleInputChange('nickname', e.target.value)}
                    error={!!errors.nickname}
                    helperText={errors.nickname || '可以留空，我们会保护您的隐私'}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)'
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)'
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="您的意见或建议 *"
                    placeholder="请详细描述您的意见、建议或遇到的问题..."
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    error={!!errors.content}
                    helperText={errors.content || `${formData.content.length}/2000 字符`}
                    variant="outlined"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)'
                        },
                        '&.Mui-focused': {
                          backgroundColor: 'white',
                          boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)'
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Card 
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      border: '2px solid',
                      borderColor: 'rgba(102, 126, 234, 0.2)',
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(102, 126, 234, 0.4)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          mb: 1
                        }}
                      >
                        网站评分 *
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mb: 3, lineHeight: 1.6 }}
                      >
                        请为我们的网站打分（1-10分）
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 3,
                        flexWrap: 'wrap'
                      }}>
                        <Rating
                          name="rating"
                          value={formData.rating}
                          onChange={(event, newValue) => {
                            handleInputChange('rating', newValue || 1);
                          }}
                          max={10}
                          size="large"
                          sx={{
                            '& .MuiRating-iconFilled': {
                              color: '#ffd700'
                            },
                            '& .MuiRating-iconHover': {
                              color: '#ffed4e'
                            }
                          }}
                        />
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }}>
                          {formData.rating}/10
                        </Box>
                      </Box>
                      {errors.rating && (
                        <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                          {errors.rating}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      disabled={loading}
                      sx={{ 
                        px: 6, 
                        py: 2,
                        borderRadius: 3,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': {
                          background: 'rgba(0, 0, 0, 0.12)'
                        }
                      }}
                    >
                      {loading ? '提交中...' : '提交反馈'}
                    </Button>
                  </Box>
                </Grid>
          </Grid>
        </form>

            <Box sx={{ 
              mt: 5, 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                opacity: 0.1
              }} />
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  textAlign: 'center',
                  fontWeight: 500,
                  lineHeight: 1.6,
                  position: 'relative',
                  zIndex: 1
                }}
              >
                💡 提示：您的反馈将帮助我们改进产品和服务，我们会认真对待每一条意见
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Feedback;